/**
 * Kanzlei-Bausteine fuer den Editor.
 *
 * Der Sinn: Ein Mitarbeiter soll einen guten Newsletter bauen koennen, ohne
 * Gestaltungsentscheidungen treffen zu muessen - und ohne aus der CI
 * ausbrechen zu koennen. Jeder Baustein bringt die freigegebenen Farben,
 * Schriften und Abstaende bereits mit.
 *
 * Alle Bausteine sind MJML. Damit erzeugt der Compiler daraus das
 * Tabellenlayout samt VML-Fallbacks, an dem handgeschriebenes HTML in Outlook
 * scheitert.
 */

/** Aus ci/design-kit/tokens.json - die maschinenlesbare Wahrheit der Marke. */
export const TOKENS = {
  primaer: '#325186',
  primaerDunkel: '#27406b',
  akzent: '#3E937B',
  akzentDunkel: '#317762',
  text: '#32373c',
  textSekundaer: '#5b6167',
  textLabel: '#6d7379',
  flaeche: '#f0f8f5',
  flaecheBlau: '#eef2f7',
  grund: '#ffffff',
  linie: '#e3e8ee',
  // E-Mail-Schrift laut Designsystem. Aptos ist die Hausschrift von
  // Microsoft 365; Calibri und sans-serif fangen aeltere Clients ab.
  schrift: 'Aptos, Calibri, Arial, sans-serif',
};

const T = TOKENS;

/**
 * Gemeinsame Textvorgaben. Die Zeilenhoehe ist bewusst grosszuegig - der
 * typische Empfaenger liest die Nachricht auf dem Telefon.
 */
const TEXT = `font-family="${T.schrift}" color="${T.text}" font-size="16px" line-height="1.6"`;

/**
 * @typedef {object} Baustein
 * @property {string} id
 * @property {string} beschriftung
 * @property {string} kategorie
 * @property {string} mjml
 */

/**
 * Erzeugt die Bausteine fuer eine konkrete Installation.
 *
 * Die Bildadressen muessen ABSOLUT sein: im Postfach des Empfaengers gibt es
 * keinen Serverkontext, ein Pfad wie /bilder/... laeuft dort ins Leere. Die
 * Basisadresse kommt deshalb vom Server und wird hier eingesetzt.
 *
 * @param {string} basisUrl
 * @returns {Baustein[]}
 */
export function bausteineFuer(basisUrl) {
  const b = `${basisUrl.replace(/\/+$/, '')}/bilder/ci`;
  return [
  {
    id: 'kanzlei-kopf',
    beschriftung: 'Kopf mit Logo',
    kategorie: 'Kanzlei',
    mjml: `<mj-section background-color="${T.grund}" padding="26px 24px 12px">
  <mj-column>
    <mj-image src="${b}/logo/logo-hauptmarke.png"
              width="132px" alt="G und W Steuerberatung" align="left" padding="0"></mj-image>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-anrede',
    beschriftung: 'Anrede',
    kategorie: 'Kanzlei',
    // Der Platzhalter erzeugt die vollstaendige Anredezeile inklusive Titel.
    mjml: `<mj-section background-color="${T.grund}" padding="8px 24px">
  <mj-column>
    <mj-text ${TEXT}>{{ anrede }},</mj-text>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-titel',
    beschriftung: 'Überschrift',
    kategorie: 'Kanzlei',
    mjml: `<mj-section background-color="${T.grund}" padding="16px 24px 0">
  <mj-column>
    <mj-text font-family="${T.schrift}" color="${T.primaer}" font-size="24px"
             font-weight="700" line-height="1.25">Überschrift</mj-text>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-absatz',
    beschriftung: 'Textabsatz',
    kategorie: 'Kanzlei',
    mjml: `<mj-section background-color="${T.grund}" padding="8px 24px">
  <mj-column>
    <mj-text ${TEXT}>Hier steht Ihr Text. Ein Gedanke pro Absatz liest sich am besten.</mj-text>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-hinweis',
    beschriftung: 'Hinweiskasten',
    kategorie: 'Kanzlei',
    mjml: `<mj-section background-color="${T.flaeche}" padding="20px 24px">
  <mj-column>
    <mj-text font-family="${T.schrift}" color="${T.text}" font-size="15px" line-height="1.6">
      <strong style="color:${T.akzentDunkel}">Hinweis:</strong>
      Wichtige Frist oder Ausnahme, die hervorgehoben gehört.
    </mj-text>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-schaltflaeche',
    beschriftung: 'Schaltfläche',
    kategorie: 'Kanzlei',
    // MJML erzeugt fuer mj-button den VML-Fallback, den Outlook braucht.
    mjml: `<mj-section background-color="${T.grund}" padding="8px 24px 24px">
  <mj-column>
    <mj-button background-color="${T.primaer}" color="${T.grund}"
               font-family="${T.schrift}" font-size="16px" font-weight="700"
               border-radius="3px" padding="8px 0" align="left"
               href="https://www.steuerservice.at">Mehr erfahren</mj-button>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-zwei-spalten',
    beschriftung: 'Zwei Spalten',
    kategorie: 'Kanzlei',
    mjml: `<mj-section background-color="${T.grund}" padding="8px 24px">
  <mj-column width="50%">
    <mj-text ${TEXT}>Linke Spalte.</mj-text>
  </mj-column>
  <mj-column width="50%">
    <mj-text ${TEXT}>Rechte Spalte.</mj-text>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-bild',
    beschriftung: 'Bild',
    kategorie: 'Kanzlei',
    // Breite und Alt-Text sind vorbelegt - genau die zwei Angaben, deren
    // Fehlen in Outlook das Layout zerlegt.
    mjml: `<mj-section background-color="${T.grund}" padding="8px 24px">
  <mj-column>
    <mj-image src="${b}/fotos/leistungen/steuerberatung.jpg"
              width="552px" alt="Bildbeschreibung eintragen" padding="0"></mj-image>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-trenner',
    beschriftung: 'Trennlinie',
    kategorie: 'Kanzlei',
    mjml: `<mj-section background-color="${T.grund}" padding="8px 24px">
  <mj-column>
    <mj-divider border-width="1px" border-color="${T.linie}" padding="8px 0"></mj-divider>
  </mj-column>
</mj-section>`,
  },
  {
    id: 'kanzlei-fuss',
    beschriftung: 'Fußzeile mit Abmeldung',
    kategorie: 'Kanzlei',
    // Der Abmeldelink ist Pflicht - ohne ihn blockiert das Gate den Versand.
    mjml: `<mj-section background-color="${T.flaecheBlau}" padding="24px">
  <mj-column>
    <mj-text font-family="${T.schrift}" color="${T.textSekundaer}" font-size="13px" line-height="1.6">
      G &amp; W Steuerberatung GmbH &middot; Wien<br />
      <a href="https://www.steuerservice.at/impressum" style="color:${T.textSekundaer}">Impressum</a> &middot;
      <a href="https://www.steuerservice.at/datenschutz" style="color:${T.textSekundaer}">Datenschutz</a>
    </mj-text>
    <mj-text font-family="${T.schrift}" color="${T.textLabel}" font-size="12px" line-height="1.6">
      Sie erhalten diese Nachricht, weil Sie sich für unseren Newsletter angemeldet haben.
      <a href="{{ abmeldelink }}" style="color:${T.textLabel}; text-decoration:underline">Newsletter abbestellen</a>
    </mj-text>
  </mj-column>
</mj-section>`,
  },
  ];
}

/**
 * Startvorlage: enthaelt bereits Kopf, Anrede und Fusszeile mit Abmeldelink,
 * damit eine neue Vorlage von Anfang an durch das Lint-Gate kommt.
 *
 * @param {string} basisUrl
 * @returns {string}
 */
export function startvorlageFuer(basisUrl) {
  const bausteine = bausteineFuer(basisUrl);
  /** @param {string} id */
  const teil = (id) => bausteine.find((baustein) => baustein.id === id)?.mjml ?? '';
  return `<mjml>
  <mj-body background-color="${T.flaeche}" width="600px">
    ${teil('kanzlei-kopf')}
    ${teil('kanzlei-anrede')}
    ${teil('kanzlei-absatz')}
    ${teil('kanzlei-fuss')}
  </mj-body>
</mjml>`;
}
